import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import faker from '@faker-js/faker';

import { BackgroundImage, Flex, Box, Text, ProgressBar } from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';

import { CARD_TYPES } from './CourseCard';

const ImageWrapper = styled(Box)`
  position: relative;
  padding-top: 56.25%;
`;

const CardContainer = styled((props) => (
  <Flex as={'a'} width={1} flexDirection={'column'} bg={'white'} {...props} />
))`
  background-color: ${getTheme('colors.white')};
  box-shadow: 0 2px 4px 0px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          min-height: 218px;
        `
      : css`
          min-height: 330px;
        `}
`;

const CourseImage = styled(BackgroundImage)`
  position: absolute;
  top: 0;
  width: 100%;
  background-position: center;
  padding: 0;
  height: 100%;
`;

const CardDetailsContainer = styled((props) => (
  <Flex width={1} flexDirection={'column'} justifyContent={'space-between'} {...props} />
))`
  overflow: hidden;
  height: 168px;
  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          padding: ${getTheme('space.2')}px;
        `
      : css`
          padding: ${getTheme('space.4')}px;
        `}
`;

const CardTitle = styled((props) => <Text color={'text'} mt={2} fontWeight={'bold'} {...props} />)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  width: 100%;
  overflow: hidden;
  ${({ variant }) =>
    variant === CARD_TYPES.SMALL
      ? css`
          font-size: ${getTheme('fontSizes.1')}px;
          -webkit-line-clamp: 3;
        `
      : css`
          font-size: ${getTheme('fontSizes.4')}px;
          -webkit-line-clamp: 2;
        `}
`;

const CardDetails = ({ title, progress, progressLabel, variant }) => {
  return (
    <CardDetailsContainer variant={variant}>
      <CardTitle variant={variant}>{title}</CardTitle>
      <ProgressBar
        pb={variant === CARD_TYPES.SMALL ? 4 : 0}
        value={progress}
        label={progressLabel}
        textProps={{
          fontWeight: 'light',
          fontSize: variant === CARD_TYPES.SMALL ? 0 : 1,
          mt: 1,
        }}
        labelPosition={ProgressBar.LABEL_POSITION.BOTTOM}
      />
    </CardDetailsContainer>
  );
};

const CourseCardVerticalWithProgress = ({
  imageUrl,
  defaultImageUrl,
  title,
  onClick,
  variant,
  progress,
  containerProps,
  name,
}) => {
  const { t } = useTranslation('products');

  return (
    <Fragment>
      <CardContainer
        data-testid={`${name}-open`}
        variant={variant}
        p={0}
        onClick={onClick}
        {...containerProps}
      >
        <ImageWrapper>
          <CourseImage variant={variant} src={imageUrl || defaultImageUrl} />
        </ImageWrapper>
        <CardDetails
          variant={variant}
          title={title}
          progress={progress}
          progressLabel={t('COURSE_CARD.PROGRESS_SUFFIX')}
        />
      </CardContainer>
    </Fragment>
  );
};

CourseCardVerticalWithProgress.propTypes = {
  imageUrl: PropTypes.string,
  defaultImageUrl: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  progress: PropTypes.number,
  name: PropTypes.string,
};

CourseCardVerticalWithProgress.defaultProps = {
  variant: 'DEFAULT',
  onClick: () => {},
  name: faker.random.uuid(),
};

export default CourseCardVerticalWithProgress;
